import React, { useMemo, useState } from "react";
import {
  Box,
  Group,
  Stack,
  Text,
  Title,
  TextInput,
  UnstyledButton,
  ScrollArea,
  Loader,
  Center,
  Button,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconSearch, IconMessage2, IconMessagePlus } from "@tabler/icons-react";
import type { Conversation } from "../../types/messaging";
import { ConversationAvatar } from "./ConversationAvatar";
import { NewChatModal } from "./NewChatModal";
import {
  conversationDisplayName,
  conversationSubtitle,
  formatPhone,
} from "./chatHelpers";
import { timeSince } from "../../utils/timeSince";
import styles from "./ConversationList.module.css";

interface ConversationListProps {
  conversations: Conversation[];
  loading: boolean;
  activeConversationId?: string;
  onSelect: (conversationId: string) => void;
}

function matchesSearch(conv: Conversation, q: string): boolean {
  if (!q) return true;
  const haystack = [
    conversationDisplayName(conv),
    conv.client_phone_e164,
    formatPhone(conv.client_phone_e164),
    conv.last_message_preview || "",
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(q.toLowerCase());
}

export const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  loading,
  activeConversationId,
  onSelect,
}) => {
  const [search, setSearch] = useState("");
  const [newChatOpen, newChat] = useDisclosure(false);
  const filtered = useMemo(
    () => conversations.filter((c) => matchesSearch(c, search.trim())),
    [conversations, search],
  );

  return (
    <Box className={styles.pane}>
      <Box className={styles.header}>
        <Group justify="space-between" wrap="nowrap">
          <Title order={3} className={styles.headerTitle}>
            Messages
          </Title>
          <Button
            variant="light"
            color="teal"
            size="xs"
            leftSection={<IconMessagePlus size={16} />}
            aria-label="New Chat"
            onClick={newChat.open}
          >
            New Chat
          </Button>
        </Group>
        <TextInput
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          placeholder="Search chats"
          leftSection={<IconSearch size={16} />}
          aria-label="Search chats"
          mt="sm"
        />
      </Box>

      <ScrollArea className={styles.scroll} type="hover">
        {loading && conversations.length === 0 ? (
          <Center py="xl">
            <Loader size="sm" />
          </Center>
        ) : filtered.length === 0 ? (
          <Center py="xl" px="md">
            <Stack align="center" gap="xs">
              <IconMessage2 size={32} stroke={1.5} opacity={0.5} />
              <Text c="dimmed" size="sm" ta="center">
                {conversations.length === 0
                  ? "No conversations yet."
                  : "No chats match your search."}
              </Text>
            </Stack>
          </Center>
        ) : (
          filtered.map((conv) => {
            const active = conv.conversation_id === activeConversationId;
            return (
              <UnstyledButton
                key={conv.conversation_id}
                className={`${styles.row} ${active ? styles.rowActive : ""}`}
                onClick={() => onSelect(conv.conversation_id)}
                aria-current={active ? "true" : undefined}
              >
                <Group gap="sm" wrap="nowrap" align="flex-start">
                  <ConversationAvatar conversation={conv} size={44} />
                  <Stack gap={2} className={styles.rowBody}>
                    <Group gap="xs" justify="space-between" wrap="nowrap">
                      <Text className={styles.rowName} truncate>
                        {conversationDisplayName(conv)}
                      </Text>
                      <Text className={styles.rowTime}>
                        {timeSince(conv.last_message_at)}
                      </Text>
                    </Group>
                    <Text className={styles.rowPreview} truncate>
                      {conv.last_message_preview || conversationSubtitle(conv)}
                    </Text>
                  </Stack>
                  {/* Unread dot: trailing the whole row, vertically centered
                      against the avatar (align-self in CSS) rather than sitting
                      on the preview line. */}
                  {conv.is_unread && (
                    <Box className={styles.unreadDot} aria-label="Unread" />
                  )}
                </Group>
              </UnstyledButton>
            );
          })
        )}
      </ScrollArea>

      <NewChatModal
        opened={newChatOpen}
        onClose={newChat.close}
        onCreated={(id) => {
          newChat.close();
          onSelect(id);
        }}
      />
    </Box>
  );
};

export default ConversationList;
